import { Link, useNavigate } from 'react-router-dom';
import {
  BadgeCheck, Building2, TrendingUp, AlertTriangle, Zap, Wrench, BellRing,
  Users, ArrowRight, CalendarClock,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date, humanise, relative } from '@/lib/format';
import {
  Badge, Card, CardHeader, EmptyState, LoadingBlock, PageHeader, Stat, Button, statusTone,
} from '@/components/ui';
import { GettingStarted } from '@/components/GettingStarted';
import type { DashboardStats } from '@shared/types';

interface Stats extends DashboardStats {
  recent_activity: {
    action: string; entity: string; actor_label: string | null;
    detail: Record<string, any>; created_at: string;
  }[];
}

export default function Dashboard() {
  const { staff, currency } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error } = useApi<Stats>('/dashboard');

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Dashboard" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-32" />)}
        </div>
        <LoadingBlock rows={5} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Could not load the dashboard"
        message={error ?? 'Please try again.'}
        action={<Button onClick={() => window.location.reload()}>Retry</Button>}
      />
    );
  }

  const collectionRate = data.billed_this_month_cents > 0
    ? Math.round((data.collected_this_month_cents / data.billed_this_month_cents) * 100)
    : 0;

  const firstName = staff?.full_name.split(' ')[0] ?? 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // Things that need a human decision today.
  const actions = [
    data.pending_verification_count > 0 && {
      to: '/app/payments',
      icon: BadgeCheck,
      tone: 'warn' as const,
      title: `${data.pending_verification_count} payment${data.pending_verification_count === 1 ? '' : 's'} to verify`,
      body: `${money(data.pending_verification_cents, currency)} claimed but not yet confirmed`,
    },
    data.reminder_queue_count > 0 && {
      to: '/app/reminders',
      icon: BellRing,
      tone: 'info' as const,
      title: `${data.reminder_queue_count} tenant${data.reminder_queue_count === 1 ? '' : 's'} flagged for a reminder`,
      body: 'Review the queue and choose who to message',
    },
    data.unpaid_bills_count > 0 && {
      to: '/app/bills',
      icon: Zap,
      tone: 'neutral' as const,
      title: `${data.unpaid_bills_count} unpaid bill${data.unpaid_bills_count === 1 ? '' : 's'}`,
      body: `${money(data.unpaid_bills_cents, currency)} owed to providers`,
    },
    data.open_maintenance_count > 0 && {
      to: '/app/maintenance',
      icon: Wrench,
      tone: 'neutral' as const,
      title: `${data.open_maintenance_count} open maintenance request${data.open_maintenance_count === 1 ? '' : 's'}`,
      body: 'Reported by tenants or logged by staff',
    },
    data.leases_expiring_60d > 0 && {
      to: '/app/tenants',
      icon: CalendarClock,
      tone: 'neutral' as const,
      title: `${data.leases_expiring_60d} lease${data.leases_expiring_60d === 1 ? '' : 's'} ending within 60 days`,
      body: 'Start renewal conversations early',
    },
  ].filter(Boolean) as { to: string; icon: any; tone: any; title: string; body: string }[];

  const chartData = data.collection_trend.map((t) => ({
    month: new Date(`${t.month}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }),
    Billed: t.billed_cents / 100,
    Collected: t.collected_cents / 100,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${greeting}, ${firstName}`}
        subtitle={`${data.property_count} propert${data.property_count === 1 ? 'y' : 'ies'} · ${data.active_tenants} active tenants · ${data.occupancy_rate}% occupied`}
        action={
          <Link to="/app/payments">
            <Button variant="primary" icon={<BadgeCheck className="h-4 w-4" />}>
              Verify payments
            </Button>
          </Link>
        }
      />

      {/*
        Above the figures on purpose. An account that is not set up yet has no
        figures worth reading, and the guide is the only thing on this screen
        that helps.
      */}
      <GettingStarted />

      {/* Headline figures */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Collected this month"
          value={money(data.collected_this_month_cents, currency)}
          hint={`${collectionRate}% of ${money(data.billed_this_month_cents, currency)} billed`}
          icon={<TrendingUp className="h-4 w-4" />}
          tone="ok"
        />
        <Stat
          label="Outstanding"
          value={money(data.outstanding_cents, currency)}
          hint={data.overdue_cents > 0 ? `${money(data.overdue_cents, currency)} of it overdue` : 'Nothing overdue'}
          tone={data.overdue_cents > 0 ? 'danger' : undefined}
          icon={<AlertTriangle className="h-4 w-4" />}
          onClick={() => navigate('/app/charges')}
        />
        <Stat
          label="Awaiting verification"
          value={String(data.pending_verification_count)}
          hint={money(data.pending_verification_cents, currency)}
          tone={data.pending_verification_count > 0 ? 'warn' : undefined}
          icon={<BadgeCheck className="h-4 w-4" />}
          onClick={() => navigate('/app/payments')}
        />
        <Stat
          label="Occupancy"
          value={`${data.occupancy_rate}%`}
          hint={`${data.occupied_count} of ${data.room_count} rooms · ${data.vacant_count} vacant`}
          icon={<Building2 className="h-4 w-4" />}
          onClick={() => navigate('/app/rooms')}
        />
      </div>

      {/* What needs attention */}
      {actions.length > 0 && (
        <Card>
          <CardHeader title="Needs your attention" subtitle="Nothing here happens automatically" />
          <div className="p-5 sm:p-6 pt-4 grid gap-3 sm:grid-cols-2">
            {actions.map((a) => (
              <Link
                key={a.to + a.title}
                to={a.to}
                className="flex items-start gap-3 p-4 rounded-xl border border-line bg-raised/40
                           hover:border-faint hover:bg-raised transition-all group"
              >
                <div className="h-9 w-9 rounded-lg bg-surface border border-line flex items-center
                                justify-center shrink-0 text-muted">
                  <a.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">{a.title}</p>
                  <p className="text-xs text-muted mt-0.5">{a.body}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-faint group-hover:text-ink shrink-0 mt-1 transition-colors" />
              </Link>
            ))}
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Collection trend */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Billed vs collected"
            subtitle="Last six months"
          />
          <div className="p-5 sm:p-6 pt-4">
            {chartData.every((d) => d.Billed === 0 && d.Collected === 0) ? (
              <EmptyState
                icon={<TrendingUp className="h-6 w-6" />}
                title="No rent history yet"
                message="Generate rent charges from a lease to start tracking collection."
              />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" vertical={false} />
                    <XAxis
                      dataKey="month"
                      stroke="rgb(var(--faint))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="rgb(var(--faint))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      width={64}
                      tickFormatter={(v: number) => money(v * 100, currency, { compact: true })}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgb(var(--raised))' }}
                      contentStyle={{
                        background: 'rgb(var(--surface))',
                        border: '1px solid rgb(var(--line))',
                        borderRadius: 12,
                        fontSize: 13,
                        color: 'rgb(var(--ink))',
                      }}
                      formatter={(v: number) => money(v * 100, currency)}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Bar dataKey="Billed" fill="rgb(var(--line))" radius={[6, 6, 0, 0]} maxBarSize={44} />
                    <Bar dataKey="Collected" fill="rgb(var(--brand))" radius={[6, 6, 0, 0]} maxBarSize={44} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        {/* Per-property outstanding */}
        <Card>
          <CardHeader title="By property" subtitle="Outstanding balance" />
          <div className="p-5 sm:p-6 pt-4">
            {data.rent_by_property.length === 0 ? (
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title="No properties yet"
                action={<Link to="/app/properties"><Button variant="primary" size="sm">Add a property</Button></Link>}
              />
            ) : (
              <div className="space-y-4">
                {data.rent_by_property.slice(0, 8).map((p) => {
                  const total = p.collected_cents + p.outstanding_cents;
                  const pct = total > 0 ? (p.collected_cents / total) * 100 : 0;
                  return (
                    <div key={p.property}>
                      <div className="flex items-baseline justify-between gap-2 mb-1.5">
                        <span className="text-sm font-medium truncate">{p.property}</span>
                        <span className={`text-sm tnum shrink-0 ${p.outstanding_cents > 0 ? 'text-danger' : 'text-muted'}`}>
                          {money(p.outstanding_cents, currency, { compact: true })}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-raised overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand transition-all"
                          style={{ width: `${Math.max(pct, total > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Activity trail */}
      <Card>
        <CardHeader
          title="Recent activity"
          subtitle="Every money decision is recorded"
          action={<Link to="/app/settings" className="text-sm link">View audit log</Link>}
        />
        <div className="p-5 sm:p-6 pt-4">
          {!data.recent_activity?.length ? (
            <EmptyState icon={<Users className="h-6 w-6" />} title="No activity yet" />
          ) : (
            <div className="space-y-1">
              {data.recent_activity.map((a, i) => (
                <div key={i} className="flex items-center gap-3 py-2 text-sm">
                  <Badge tone={statusTone(a.action)} className="shrink-0 min-w-[92px] justify-center">
                    {humanise(a.action)}
                  </Badge>
                  <span className="text-muted truncate flex-1">
                    <span className="text-ink">{a.actor_label ?? 'System'}</span>
                    {' · '}{humanise(a.entity)}
                    {a.detail?.name ? ` · ${a.detail.name}` : ''}
                    {a.detail?.tenant ? ` · ${a.detail.tenant}` : ''}
                    {a.detail?.amount_cents ? ` · ${money(Number(a.detail.amount_cents), currency)}` : ''}
                  </span>
                  <span className="text-xs text-faint shrink-0" title={date(a.created_at)}>
                    {relative(a.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
